import { c as _c } from "react/compiler-runtime";
import { CarouselNavigation } from "@/components/carousel-navigation";
import { SellerUnitOne } from "@/components/sup/one";
import { SellerUnitOneSkeleton } from "@/components/sup/skeletons/seller-unit-one-skeleton";
import { CarouselHeaderSkeleton, CarouselNavigationSkeleton } from "@/components/ui/skeleton";
import { useSupOne } from "@/hooks/use-sup-data";
import { usePropertyInteractionStore } from "@/stores/property-interaction-store";
import { MapItem, MapItemSUP } from "@/types/search";
import type { SellerUnitAndProject, SellerUnitsAndProjectsOne } from "@/types/sup";
import { propertyUrlSync } from "@/utils/property-url-sync";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
interface PropertyDetailsCarouselProps {
  supIds: string[];
  projectName?: string | null;
  onToggleFavorite?: (property: MapItemSUP) => void;
  onShortlist?: (property: MapItemSUP) => void;
  className?: string;
  isForPreference?: boolean; // When true, disables search page store integration
}

// Memoized carousel item component for better performance
const CarouselItem = memo(t0 => {
  const $ = _c(11);
  const {
    sup,
    position,
    isLoaded,
    slideDirection
  } = t0;
  if (!sup) {
    return null;
  }
  let translateX = "0%";
  let opacity = 1;
  if (position === "prev") {
    translateX = slideDirection === "right" ? "0%" : "-100%";
    opacity = slideDirection === "right" ? 1 : 0;
  } else {
    if (position === "current") {
      if (slideDirection === "left") {
        translateX = "-100%";
        opacity = 0;
      } else {
        if (slideDirection === "right") {
          translateX = "100%";
          opacity = 0;
        } else {
          translateX = "0%";
          opacity = 1;
        }
      }
    } else {
      if (position === "next") {
        translateX = slideDirection === "left" ? "0%" : "100%";
        opacity = slideDirection === "left" ? 1 : 0;
      }
    }
  }
  const t1 = `${sup._id}-${position}`;
  const t2 = `translateX(${translateX})`;
  let t3;
  if ($[0] !== opacity || $[1] !== t2) {
    t3 = {
      transform: t2,
      opacity,
      willChange: "transform, opacity"
    };
    $[0] = opacity;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== isLoaded || $[4] !== position || $[5] !== sup) {
    t4 = sup && isLoaded ? <SellerUnitOne supOne={sup} className="border-0 shadow-none rounded-none" isLoading={false} isInModal={true} isVisible={position === "current"} /> : <SellerUnitOneSkeleton isInModal={true} />;
    $[3] = isLoaded;
    $[4] = position;
    $[5] = sup;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t1 || $[8] !== t3 || $[9] !== t4) {
    t5 = <div key={t1} className="absolute top-0 left-0 w-full h-full transition-all duration-300 ease-in-out" style={t3}>{t4}</div>;
    $[7] = t1;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
});
CarouselItem.displayName = "CarouselItem";
export function PropertyDetailsCarousel(t0) {
  const $ = _c(100);
  const {
    supIds,
    projectName,
    className: t1,
    isForPreference: t2
  } = t0;
  const className = t1 === undefined ? "" : t1;
  const isForPreference = t2 === undefined ? false : t2;
  const overlaySupId = usePropertyInteractionStore(_temp);
  const addViewedProperty = usePropertyInteractionStore(_temp2);
  let t3;
  bb0: {
    if (!overlaySupId || isForPreference) {
      t3 = supIds;
      break bb0;
    }
    let t4;
    if ($[0] !== overlaySupId || $[1] !== supIds) {
      let t5;
      if ($[3] !== overlaySupId) {
        t5 = id => id !== overlaySupId;
        $[3] = overlaySupId;
        $[4] = t5;
      } else {
        t5 = $[4];
      }
      t4 = [overlaySupId, ...supIds.filter(t5)];
      $[0] = overlaySupId;
      $[1] = supIds;
      $[2] = t4;
    } else {
      t4 = $[2];
    }
    t3 = t4;
  }
  const sortedSupIds = t3;
  let t4;
  if ($[5] !== isForPreference || $[6] !== sortedSupIds) {
    t4 = () => {
      if (isForPreference) {
        return 0;
      }
      const currentPropertyId = propertyUrlSync.getCurrentPropertyId();
      if (currentPropertyId) {
        const index = sortedSupIds.findIndex(id_0 => id_0 === currentPropertyId);
        return index >= 0 ? index : 0;
      }
      return 0;
    };
    $[5] = isForPreference;
    $[6] = sortedSupIds;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const getInitialIndex = t4;
  const [currentIndex, setCurrentIndex] = useState(getInitialIndex);
  const [slideDirection, setSlideDirection] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const currentPropertyIdRef = useRef(null);
  const initialViewedRef = useRef(false);
  const isOpen = usePropertyInteractionStore(_temp3);
  let t5;
  if ($[8] !== sortedSupIds) {
    const mapData = [];
    t5 = mapData.length > 0 ? sortedSupIds.map(id_1 => {
      for (const mapItem of mapData) {
        const foundSup = mapItem.sups.find(sup => sup._id === id_1);
        if (foundSup) {
          return {
            ...foundSup,
            projectName: mapItem.projectName,
            projectId: mapItem.projectId,
            location: {
              ...foundSup.location,
              gps: {
                type: "Point" as const,
                coordinates: Array.isArray(mapItem._id) ? mapItem._id : String(mapItem._id).split(",").map(Number) as [number, number]
              }
            }
          };
        }
      }
      return null;
    }).filter(_temp4) : [];
    $[8] = sortedSupIds;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const initialData = t5;
  let t6;
  if ($[10] !== sortedSupIds) {
    t6 = index_0 => {
      const priorityIds = [];
      const totalItems = sortedSupIds.length;
      priorityIds.push(sortedSupIds[index_0]);
      if (totalItems > 1) {
        const prevIndex = index_0 === 0 ? totalItems - 1 : index_0 - 1;
        const nextIndex = index_0 === totalItems - 1 ? 0 : index_0 + 1;
        priorityIds.push(sortedSupIds[nextIndex]);
        priorityIds.push(sortedSupIds[prevIndex]);
      }
      sortedSupIds.forEach(id_2 => {
        if (!priorityIds.includes(id_2)) {
          priorityIds.push(id_2);
        }
      });
      return priorityIds;
    };
    $[10] = sortedSupIds;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const getPriorityIds = t6;
  let t7;
  if ($[12] !== currentIndex || $[13] !== getPriorityIds) {
    t7 = getPriorityIds(currentIndex);
    $[12] = currentIndex;
    $[13] = getPriorityIds;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const priorityIds_0 = t7;
  const initData = initialData.length > 0 ? initialData : undefined;
  const {
    data: sups,
    loading,
    initialLoading,
    error
  } = useSupOne(priorityIds_0, initData as SellerUnitAndProject[] | undefined);
  let t8;
  bb1: {
    if (sups.length === 0) {
      let t9;
      if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
        t9 = [];
        $[15] = t9;
      } else {
        t9 = $[15];
      }
      t8 = t9;
      break bb1;
    }
    let t9;
    if ($[16] !== sortedSupIds || $[17] !== sups) {
      let t10;
      if ($[19] !== sups) {
        t10 = id_3 => sups.find(sup_1 => sup_1._id === id_3);
        $[19] = sups;
        $[20] = t10;
      } else {
        t10 = $[20];
      }
      t9 = sortedSupIds.map(t10).filter(_temp5);
      $[16] = sortedSupIds;
      $[17] = sups;
      $[18] = t9;
    } else {
      t9 = $[18];
    }
    t8 = t9;
  }
  const orderedSups = t8;
  let t9;
  if ($[21] !== sortedSupIds) {
    t9 = state_2 => sortedSupIds.filter(id_4 => state_2.viewedProperties.has(id_4)).length;
    $[21] = sortedSupIds;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const viewCount = usePropertyInteractionStore(t9);
  let t10;
  let t11;
  if ($[23] !== addViewedProperty || $[24] !== currentIndex || $[25] !== sortedSupIds) {
    t10 = () => {
      if (!initialViewedRef.current) {
        const initialPropertyId = sortedSupIds[currentIndex];
        const {
          viewedProperties
        } = usePropertyInteractionStore.getState();
        if (initialPropertyId && !viewedProperties.has(initialPropertyId)) {
          addViewedProperty(initialPropertyId);
        }
        initialViewedRef.current = true;
      }
    };
    t11 = [sortedSupIds, currentIndex, addViewedProperty];
    $[23] = addViewedProperty;
    $[24] = currentIndex;
    $[25] = sortedSupIds;
    $[26] = t10;
    $[27] = t11;
  } else {
    t10 = $[26];
    t11 = $[27];
  }
  useEffect(t10, t11);
  const currentPropertyId_0 = orderedSups[currentIndex]?._id;
  let t12;
  let t13;
  if ($[28] !== addViewedProperty || $[29] !== currentPropertyId_0 || $[30] !== isOpen) {
    t12 = () => {
      if (currentPropertyId_0 && isOpen) {
        const {
          viewedProperties: viewedProperties_0
        } = usePropertyInteractionStore.getState();
        if (!viewedProperties_0.has(currentPropertyId_0)) {
          addViewedProperty(currentPropertyId_0);
        }
        if (currentPropertyIdRef.current !== currentPropertyId_0) {
          propertyUrlSync.updatePropertyIdDebounced(currentPropertyId_0);
          currentPropertyIdRef.current = currentPropertyId_0;
        }
      }
    };
    t13 = [currentPropertyId_0, isOpen, addViewedProperty];
    $[28] = addViewedProperty;
    $[29] = currentPropertyId_0;
    $[30] = isOpen;
    $[31] = t12;
    $[32] = t13;
  } else {
    t12 = $[31];
    t13 = $[32];
  }
  useEffect(t12, t13);
  let t14;
  if ($[33] !== isTransitioning || $[34] !== orderedSups.length) {
    t14 = () => {
      if (isTransitioning) {
        return;
      }
      setIsTransitioning(true);
      setSlideDirection("left");
      setCurrentIndex(prev => prev === 0 ? orderedSups.length - 1 : prev - 1);
      setTimeout(() => {
        setSlideDirection(null);
        setIsTransitioning(false);
      }, 300);
    };
    $[33] = isTransitioning;
    $[34] = orderedSups.length;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  const goToPrevious = t14;
  let t15;
  if ($[36] !== isTransitioning || $[37] !== orderedSups.length) {
    t15 = () => {
      if (isTransitioning) {
        return;
      }
      setIsTransitioning(true);
      setSlideDirection("right");
      setCurrentIndex(prev_0 => prev_0 > orderedSups.length - 1 ? 0 : prev_0 + 1);
      setTimeout(() => {
        setSlideDirection(null);
        setIsTransitioning(false);
      }, 300);
    };
    $[36] = isTransitioning;
    $[37] = orderedSups.length;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  const goToNext = t15;
  let t16;
  if ($[39] !== currentIndex || $[40] !== isTransitioning) {
    t16 = index_1 => {
      if (index_1 === currentIndex || isTransitioning) {
        return;
      }
      setIsTransitioning(true);
      const direction = index_1 > currentIndex ? "right" : "left";
      setSlideDirection(direction);
      setCurrentIndex(index_1);
      setTimeout(() => {
        setSlideDirection(null);
        setIsTransitioning(false);
      }, 300);
    };
    $[39] = currentIndex;
    $[40] = isTransitioning;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  const goToIndex = t16;
  let t17;
  if ($[42] !== goToNext || $[43] !== goToPrevious || $[44] !== isTransitioning || $[45] !== orderedSups.length) {
    t17 = event => {
      if (orderedSups.length <= 1 || isTransitioning) {
        return;
      }
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        goToPrevious();
      } else {
        if (event.key === "ArrowRight") {
          event.preventDefault();
          goToNext();
        }
      }
    };
    $[42] = goToNext;
    $[43] = goToPrevious;
    $[44] = isTransitioning;
    $[45] = orderedSups.length;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  const handleKeyDown = t17;
  let t18;
  let t19;
  if ($[47] !== handleKeyDown) {
    t18 = () => {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    };
    t19 = [handleKeyDown];
    $[47] = handleKeyDown;
    $[48] = t18;
    $[49] = t19;
  } else {
    t18 = $[48];
    t19 = $[49];
  }
  useEffect(t18, t19);
  let t20;
  if ($[50] !== currentIndex || $[51] !== orderedSups) {
    t20 = () => {
      if (orderedSups.length === 0) {
        return [];
      }
      if (orderedSups.length === 1) {
        return [{
          sup: orderedSups[0],
          position: "current" as const,
          isLoaded: !!orderedSups[0]
        }];
      }
      const prevIndex_0 = currentIndex === 0 ? orderedSups.length - 1 : currentIndex - 1;
      const nextIndex_0 = currentIndex === orderedSups.length - 1 ? 0 : currentIndex + 1;
      return [{
        sup: orderedSups[prevIndex_0],
        position: "prev" as const,
        isLoaded: !!orderedSups[prevIndex_0]
      }, {
        sup: orderedSups[currentIndex],
        position: "current" as const,
        isLoaded: !!orderedSups[currentIndex]
      }, {
        sup: orderedSups[nextIndex_0],
        position: "next" as const,
        isLoaded: !!orderedSups[nextIndex_0]
      }].filter(_temp6);
    };
    $[50] = currentIndex;
    $[51] = orderedSups;
    $[52] = t20;
  } else {
    t20 = $[52];
  }
  const getVisibleSups = t20;
  let t21;
  if ($[53] !== getVisibleSups) {
    t21 = getVisibleSups();
    $[53] = getVisibleSups;
    $[54] = t21;
  } else {
    t21 = $[54];
  }
  const visibleSups = t21;
  if (error || !loading && !initialLoading && orderedSups?.length === 0) {
    const t22 = `bg-white rounded-lg shadow-xl border border-gray-200 w-full ${className}`;
    let t23;
    if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
      t23 = <div className="flex items-center justify-center h-64"><div className="text-gray-500">Unable to fetch property details</div></div>;
      $[55] = t23;
    } else {
      t23 = $[55];
    }
    let t24;
    if ($[56] !== t22) {
      t24 = <div className={t22}>{t23}</div>;
      $[56] = t22;
      $[57] = t24;
    } else {
      t24 = $[57];
    }
    return t24;
  }
  if (initialLoading && orderedSups?.length === 0) {
    const t22 = `bg-white rounded-lg shadow-xl border border-gray-200 w-full ${className}`;
    let t23;
    let t24;
    let t25;
    if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
      t23 = <CarouselHeaderSkeleton />;
      t24 = <CarouselNavigationSkeleton />;
      t25 = <SellerUnitOneSkeleton isInModal={true} />;
      $[58] = t23;
      $[59] = t24;
      $[60] = t25;
    } else {
      t23 = $[58];
      t24 = $[59];
      t25 = $[60];
    }
    let t26;
    if ($[61] !== t22) {
      t26 = <div className={t22}>{t23}{t24}{t25}</div>;
      $[61] = t22;
      $[62] = t26;
    } else {
      t26 = $[62];
    }
    return t26;
  }
  if (orderedSups.length === 1 && orderedSups[0]) {
    const t22 = `bg-white rounded-lg shadow-xl border border-gray-200 w-full ${className} relative`;
    let t23;
    if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
      t23 = {
        height: "calc(100dvh - 94px)"
      };
      $[63] = t23;
    } else {
      t23 = $[63];
    }
    let t24;
    if ($[64] !== orderedSups[0]) {
      t24 = <div className="md:contents overflow-y-auto overflow-x-hidden md:overflow-visible" style={t23}><SellerUnitOne supOne={orderedSups[0]} className="border-0 shadow-none rounded-none" isLoading={false} isInModal={true} /></div>;
      $[64] = orderedSups[0];
      $[65] = t24;
    } else {
      t24 = $[65];
    }
    let t25;
    if ($[66] !== t22 || $[67] !== t24) {
      t25 = <div className={t22}>{t24}</div>;
      $[66] = t22;
      $[67] = t24;
      $[68] = t25;
    } else {
      t25 = $[68];
    }
    return t25;
  }
  const t22 = `bg-white rounded-lg shadow-xl border border-gray-200 w-full ${className} relative`;
  const t23 = `/projects/${orderedSups[0]?.projectId}`;
  const t24 = projectName || orderedSups[0]?.projectName || "Properties";
  let t25;
  if ($[69] !== t24) {
    t25 = <h3 className="font-semibold text-gray-900">{t24}</h3>;
    $[69] = t24;
    $[70] = t25;
  } else {
    t25 = $[70];
  }
  let t26;
  if ($[71] !== t23 || $[72] !== t25) {
    t26 = <a href={t23} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline">{t25}</a>;
    $[71] = t23;
    $[72] = t25;
    $[73] = t26;
  } else {
    t26 = $[73];
  }
  const t27 = currentIndex + 1;
  const t28 = viewCount > 0 && ` • Viewed ${viewCount}`;
  let t29;
  if ($[74] !== sortedSupIds.length || $[75] !== t27 || $[76] !== t28) {
    t29 = <p className="text-sm text-gray-500">{t27} of {sortedSupIds.length}{t28}</p>;
    $[74] = sortedSupIds.length;
    $[75] = t27;
    $[76] = t28;
    $[77] = t29;
  } else {
    t29 = $[77];
  }
  let t30;
  if ($[78] !== t26 || $[79] !== t29) {
    t30 = <div className="flex items-center justify-between p-2 pt-4 border-b">{t26}{t29}</div>;
    $[78] = t26;
    $[79] = t29;
    $[80] = t30;
  } else {
    t30 = $[80];
  }
  const t31 = orderedSups.length || sortedSupIds.length;
  let t32;
  if ($[81] !== currentIndex || $[82] !== goToIndex || $[83] !== goToNext || $[84] !== goToPrevious || $[85] !== isTransitioning || $[86] !== t31) {
    t32 = <CarouselNavigation currentIndex={currentIndex} totalItems={t31} onPrevious={goToPrevious} onNext={goToNext} onGoToIndex={goToIndex} disabled={isTransitioning} />;
    $[81] = currentIndex;
    $[82] = goToIndex;
    $[83] = goToNext;
    $[84] = goToPrevious;
    $[85] = isTransitioning;
    $[86] = t31;
    $[87] = t32;
  } else {
    t32 = $[87];
  }
  let t33;
  if ($[88] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = {
      height: "calc(100dvh - 94px)",
      touchAction: "pan-y"
    };
    $[88] = t33;
  } else {
    t33 = $[88];
  }
  let t34;
  if ($[89] !== initialLoading || $[90] !== loading || $[91] !== orderedSups.length || $[92] !== slideDirection || $[93] !== visibleSups) {
    t34 = <div className="relative overflow-y-auto overflow-x-hidden" style={t33}>{orderedSups.length === 0 && initialLoading ? <SellerUnitOneSkeleton isInModal={true} /> : <div className={`relative w-full h-full ${visibleSups.length > 1 ? "overflow-y-scroll overflow-x-hidden" : "overflow-hidden"}`}><div className="relative w-full h-full">{visibleSups.map(t35 => {
            const {
              sup: sup_3,
              position,
              isLoaded
            } = t35;
            return <CarouselItem key={`${sup_3?._id}-${position}`} sup={sup_3} position={position} isLoaded={isLoaded} slideDirection={slideDirection} loading={loading} />;
          })}</div></div>}</div>;
    $[89] = initialLoading;
    $[90] = loading;
    $[91] = orderedSups.length;
    $[92] = slideDirection;
    $[93] = visibleSups;
    $[94] = t34;
  } else {
    t34 = $[94];
  }
  let t35;
  if ($[95] !== t22 || $[96] !== t30 || $[97] !== t32 || $[98] !== t34) {
    t35 = <div className={t22}>{t30}{t32}{t34}</div>;
    $[95] = t22;
    $[96] = t30;
    $[97] = t32;
    $[98] = t34;
    $[99] = t35;
  } else {
    t35 = $[99];
  }
  return t35;
}
function _temp6(item) {
  return item.sup;
}
function _temp5(sup_2) {
  return sup_2 !== undefined;
}
function _temp4(sup_0) {
  return sup_0 !== null;
}
function _temp3(state_1) {
  return state_1.isPropertyOverlayOpen;
}
function _temp2(state_0) {
  return state_0.addViewedProperty;
}
function _temp(state) {
  return state.overlaySupId;
}